import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CallService } from '../services/callService';
import {
  CadApplyField,
  CadApplyResult,
  CadRecord,
  CadVerification,
  CadVerificationResult,
  CallAttachment,
  isAppliedAutomatically,
  isCadExtractionInProgress,
  isCadRescoreInProgress,
  isCadVerificationInProgress,
} from '../types/Call';

// Same schedule as the analysis poll on CallDetailsPage. Extraction usually lands within
// seconds; the stretched tail keeps a queued run (cad limiter busy) cheap.
const POLL_BACKOFF_MS = [4000, 4000, 4000, 8000, 8000, 15000];
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
// A record that has just been read is followed, in the same sweep pass, by the automatic check
// request and apply (leg 4c). The poll that saw it read may have read the row before those
// writes, and how long they take is not observable here, so the poll keeps reading on its
// schedule until a check is requested or an apply lands, and gives up after this window: an
// agency with both switches off costs a few unaudited list reads per extraction, nothing more.
const SETTLE_WINDOW_MS = 20 * 1000;

const pollDelay = (attempt: number) =>
  POLL_BACKOFF_MS[Math.min(attempt, POLL_BACKOFF_MS.length - 1)];

/** 4xx other than throttling/timeout means polling will never succeed. */
const isUnrecoverablePollError = (status?: number) =>
  status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;

/** A poll's answer, or the list already held when the rows are the same. */
const sameOrLatest = (prev: CallAttachment[] | null | undefined, latest: CallAttachment[] | null) =>
  prev !== undefined && JSON.stringify(prev) === JSON.stringify(latest) ? prev : latest;

const findRecord = (list: CallAttachment[] | null | undefined) =>
  list?.find((a) => a.kind === 'CadEventReport');

const extractionInProgress = (row: CallAttachment) =>
  isCadExtractionInProgress(row.extractionStatus);
const verificationInProgress = (row: CallAttachment) =>
  isCadVerificationInProgress(row.verificationStatus);
const rescoreInProgress = (row: CallAttachment) => isCadRescoreInProgress(row.rescoreStatus);

/**
 * Keeps the extraction wait reading after a successful read, for the automatic steps that follow
 * it (leg 4c): until a check is requested or an apply lands, and no longer than the settle window.
 */
const awaitingAutomaticSteps = (row: CallAttachment, firstTerminalAt: number) =>
  row.extractionStatus === 'Extracted' &&
  row.verificationStatus === 'NotRun' &&
  !row.appliedAt &&
  Date.now() - firstTerminalAt < SETTLE_WINDOW_MS;

export type CadPollState = 'idle' | 'polling' | 'timedOut' | 'unavailable';

type SetAttachments = (
  update: (prev: CallAttachment[] | null | undefined) => CallAttachment[] | null
) => void;

/**
 * A wait on one background stage (reading the report, the check against the transcript, the CAD
 * Skills rescore): polls the list, which is unaudited, until the row's stage is no longer in
 * progress (or, given `keepWaiting`, until that says the wait is over), and gives up after the
 * timeout or an unrecoverable error. Each wait has its own generation, timer and give-up flag:
 * one can run while another is idle, and stopping one must never cancel another.
 *
 * Not the stage's own endpoint: a Verified answer there carries the result and is an audited read,
 * and the hook runs while the tab is closed, so that would record a view nobody saw.
 */
function useStageWait(
  callId: string,
  isInProgress: (row: CallAttachment) => boolean,
  setAttachments: SetAttachments,
  keepWaiting?: (row: CallAttachment, firstTerminalAt: number) => boolean
) {
  const [status, setStatus] = useState<{ callId: string; state: CadPollState }>();
  const state: CadPollState = status?.callId === callId ? status.state : 'idle';
  const setState = useCallback(
    (next: CadPollState) => setStatus({ callId, state: next }),
    [callId]
  );
  // Monotonic token, as on CallDetailsPage: clearing the timer does not stop a tick already
  // awaiting its GET, so every continuation re-checks this before writing state.
  const generationRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set when the wait gives up, so the auto-start effect does not immediately restart it while
  // the row is still in progress. Cleared only by an explicit user action or a new request.
  const abandonedRef = useRef(false);

  const stop = useCallback(() => {
    generationRef.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    const generation = ++generationRef.current;
    const startedAt = Date.now();
    let attempt = 0;
    let firstTerminalAt: number | undefined;
    const isCurrent = () => generation === generationRef.current;

    const tick = async () => {
      if (!isCurrent()) return;

      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        abandonedRef.current = true;
        stop();
        setState('timedOut');
        return;
      }

      try {
        const latest = await CallService.listCadRecords(callId);
        if (!isCurrent()) return;

        setAttachments((prev) => sameOrLatest(prev, latest));
        const current = findRecord(latest);
        if (current && isInProgress(current)) {
          attempt += 1;
          timerRef.current = setTimeout(tick, pollDelay(attempt));
          return;
        }

        // The first terminal status ends the wait (leg 3 follow-up p), unless the stage has
        // something to wait for after it.
        if (current && keepWaiting) {
          firstTerminalAt ??= Date.now();
          if (keepWaiting(current, firstTerminalAt)) {
            attempt += 1;
            timerRef.current = setTimeout(tick, pollDelay(attempt));
            return;
          }
        }

        stop();
        setState('idle');
      } catch (err) {
        if (!isCurrent()) return;

        const statusCode = (err as { statusCode?: number } | undefined)?.statusCode;
        if (isUnrecoverablePollError(statusCode)) {
          abandonedRef.current = true;
          stop();
          setState('unavailable');
          return;
        }

        attempt += 1;
        timerRef.current = setTimeout(tick, pollDelay(attempt));
      }
    };

    setState('polling');
    timerRef.current = setTimeout(tick, pollDelay(0));
  }, [callId, isInProgress, keepWaiting, setAttachments, setState, stop]);

  /** Restarts the wait after it gave up. */
  const resume = useCallback(() => {
    abandonedRef.current = false;
    start();
  }, [start]);

  /**
   * Leaves the wait idle with the give-up cleared, so the auto-start effect begins it from the
   * row's state: after a new request, or after a new record replaces the old one.
   */
  const reset = useCallback(() => {
    stop();
    abandonedRef.current = false;
    setState('idle');
  }, [stop, setState]);

  return { state, start, stop, resume, reset, abandonedRef };
}

export interface UseCadRecord {
  /** undefined while the first check runs; null when the agency has CAD records off. */
  attachments: CallAttachment[] | null | undefined;
  /** The call's CAD event report, if one is attached. */
  record: CallAttachment | undefined;
  pollState: CadPollState;
  /** Upload percentage while an attach is in flight, otherwise null. Held here so leaving the tab mid-upload keeps it. */
  uploadProgress: number | null;
  /** Extracted fields for `record`, loaded on demand; each load is an audited read. */
  fields: CadRecord | undefined;
  fieldsError: boolean;
  loadFields: () => void;
  /** Resolves to the new row, or null when the attach failed (already notified). */
  attach: (file: File) => Promise<CallAttachment | null>;
  /** Attaches a report from the waiting shelf. Resolves to the new row, or null when it failed (already notified). */
  attachFromInbox: (inboxItemId: string) => Promise<CallAttachment | null>;
  retryExtraction: () => Promise<void>;
  /** Restarts status checks after a timeout or an unrecoverable poll error. */
  resumePolling: () => void;
  /** State of the wait on a check against the transcript; independent of the extraction poll. */
  verificationPollState: CadPollState;
  /** The check's result for `record`'s current verification, loaded on demand; each load is an audited read. */
  verification: CadVerificationResult | undefined;
  verificationLoadError: boolean;
  loadVerification: () => void;
  /** Queues a check (or a re-run) and starts waiting on it. */
  /** Resolves true when the check was queued; false when the request failed (already notified). */
  requestVerification: () => Promise<boolean>;
  resumeVerificationPolling: () => void;
  /** Writes the selected fields to the call record and refreshes the row's applied stamp. */
  apply: (fields: CadApplyField[]) => Promise<CadApplyResult | null>;
  /**
   * Identifies the record's current automatic apply (leg 4c), in the same shape as the fields and
   * verification keys; undefined when the last apply was a supervisor's or there was none. A change
   * while the page is open means the sweep wrote the call record with no click behind it.
   */
  appliedAutomaticallyKey: string | undefined;
  /** State of the wait on a CAD Skills rescore (leg 6); independent of the other waits. */
  rescorePollState: CadPollState;
  /** Queues the CAD Skills rescore and starts waiting on it. Resolves true when queued; false when the request failed (already notified). */
  requestRescore: () => Promise<boolean>;
  resumeRescorePolling: () => void;
  /**
   * Identifies the record's current landed rescore (attachment id and when it landed); undefined
   * until one has. A change while the page is open means the call's scores were rewritten, whether
   * from a click here or from a regeneration that re-queued it.
   */
  rescoredKey: string | undefined;
  /** Re-reads the list once, for a state change the page knows about but no wait is watching (an analysis landing). */
  refresh: () => void;
}

/**
 * The CAD record state for one call: whether the feature is on, the attachment row, extraction
 * progress, the extracted fields, the check against the transcript, apply, and the CAD Skills
 * rescore.
 *
 * Polling reads only the list endpoint, which is metadata and unaudited, so waiting on an
 * extraction writes nothing to the audit trail. The fields endpoint is a durable VIEW_CAD_RECORD
 * write, so fields are fetched once per extraction (keyed on attachment id and extractedAt) and
 * only when a view asks for them. The verification result follows the same rule, keyed on
 * attachment id and verifiedAt; its wait polls the unaudited list too, and the result is read
 * only by loadVerification. The rescore has no result of its own to read: it lands in the call's
 * scores, which the page re-reads.
 */
export function useCadRecord(callId: string): UseCadRecord {
  // Every piece of state carries the key it belongs to (the call, or the call's extraction) and
  // is read only when that key is current, so moving to another call needs no reset.
  const [listState, setListState] = useState<{ callId: string; list: CallAttachment[] | null }>();
  const [fieldsState, setFieldsState] = useState<{ key: string; fields: CadRecord }>();
  const [fieldsErrorKey, setFieldsErrorKey] = useState<string>();
  const [upload, setUpload] = useState<{ callId: string; progress: number }>();
  const [verificationState, setVerificationState] = useState<{
    key: string;
    result: CadVerificationResult;
  }>();
  const [verificationErrorKey, setVerificationErrorKey] = useState<string>();

  const attachments = listState?.callId === callId ? listState.list : undefined;
  const uploadProgress = upload?.callId === callId ? upload.progress : null;
  const setAttachments = useCallback<SetAttachments>(
    (update) =>
      setListState((prev) => {
        const current = prev?.callId === callId ? prev.list : undefined;
        const next = update(current);
        // The same list is the same state: a poll that read nothing new re-renders nothing.
        return prev && prev.callId === callId && next === current ? prev : { callId, list: next };
      }),
    [callId]
  );
  const replaceRow = useCallback(
    (id: string, update: (row: CallAttachment) => CallAttachment) =>
      setAttachments((prev) => (prev ?? []).map((a) => (a.attachmentId === id ? update(a) : a))),
    [setAttachments]
  );
  /** Copies a verification answer's status onto the row, so the row stays the one source of stage state. */
  const patchVerification = useCallback(
    (dto: CadVerification) =>
      replaceRow(dto.attachmentId, (row) => ({
        ...row,
        verificationStatus: dto.status,
        verificationError: dto.error,
        verificationModel: dto.model,
        verifiedAt: dto.verifiedAt,
      })),
    [replaceRow]
  );

  const extractWait = useStageWait(callId, extractionInProgress, setAttachments, awaitingAutomaticSteps);
  const verifyWait = useStageWait(callId, verificationInProgress, setAttachments);
  const rescoreWait = useStageWait(callId, rescoreInProgress, setAttachments);

  const fieldsRequestRef = useRef<string | undefined>(undefined);
  const verificationRequestRef = useRef<string | undefined>(undefined);

  const record = findRecord(attachments);
  const recordFieldsKey =
    record?.extractionStatus === 'Extracted'
      ? `${record.attachmentId}|${record.extractedAt}`
      : undefined;
  const recordVerificationKey =
    record?.verificationStatus === 'Verified'
      ? `${record.attachmentId}|${record.verifiedAt}`
      : undefined;
  const appliedAutomaticallyKey =
    record && isAppliedAutomatically(record)
      ? `${record.attachmentId}|${record.appliedAt}`
      : undefined;
  const rescoredKey =
    record?.rescoreStatus === 'Rescored' && record.cadScoredAt
      ? `${record.attachmentId}|${record.cadScoredAt}`
      : undefined;

  /** Stores a result under the verification it belongs to and marks that verification as read. */
  const keepVerificationResult = useCallback((dto: CadVerification) => {
    if (dto.status !== 'Verified' || !dto.result) return false;
    const key = `${dto.attachmentId}|${dto.verifiedAt}`;
    verificationRequestRef.current = key;
    setVerificationState({ key, result: dto.result });
    return true;
  }, []);

  const { stop: stopExtractWait, abandonedRef: extractAbandonedRef } = extractWait;
  const { stop: stopVerifyWait, abandonedRef: verifyAbandonedRef } = verifyWait;
  const { stop: stopRescoreWait, abandonedRef: rescoreAbandonedRef } = rescoreWait;

  // Initial check for each call.
  useEffect(() => {
    let cancelled = false;
    extractAbandonedRef.current = false;
    verifyAbandonedRef.current = false;
    rescoreAbandonedRef.current = false;

    CallService.listCadRecords(callId)
      .then((list) => {
        if (!cancelled) setAttachments(() => list);
      })
      .catch(() => {
        // The call itself loaded, so this is a transient fault; without an answer the page
        // cannot tell whether the agency has the feature, so the tab stays hidden.
        if (!cancelled) setAttachments(() => null);
      });

    return () => {
      cancelled = true;
      stopExtractWait();
      stopVerifyWait();
      stopRescoreWait();
    };
  }, [
    callId,
    setAttachments,
    stopExtractWait,
    stopVerifyWait,
    stopRescoreWait,
    extractAbandonedRef,
    verifyAbandonedRef,
    rescoreAbandonedRef,
  ]);

  // Start polling whenever the record is in flight, e.g. on arriving at a call mid-extraction.
  const recordInProgress = !!record && extractionInProgress(record);
  const { state: pollState, start: startExtractWait } = extractWait;
  useEffect(() => {
    if (recordInProgress && pollState === 'idle' && !extractAbandonedRef.current) {
      startExtractWait();
    }
  }, [recordInProgress, pollState, startExtractWait, extractAbandonedRef]);

  // The same for a check against the transcript, e.g. arriving at a call while one runs.
  const recordVerifying = !!record && verificationInProgress(record);
  const { state: verificationPollState, start: startVerifyWait } = verifyWait;
  useEffect(() => {
    if (recordVerifying && verificationPollState === 'idle' && !verifyAbandonedRef.current) {
      startVerifyWait();
    }
  }, [recordVerifying, verificationPollState, startVerifyWait, verifyAbandonedRef]);

  // And for a rescore, which a regeneration can queue with no click on this page.
  const recordRescoring = !!record && rescoreInProgress(record);
  const { state: rescorePollState, start: startRescoreWait } = rescoreWait;
  useEffect(() => {
    if (recordRescoring && rescorePollState === 'idle' && !rescoreAbandonedRef.current) {
      startRescoreWait();
    }
  }, [recordRescoring, rescorePollState, startRescoreWait, rescoreAbandonedRef]);

  // Depends on the id, not the row: every poll hands back a new row object, and a callback that
  // changed with it would re-run the tab's load effect.
  const attachmentId = record?.attachmentId;
  const loadFields = useCallback(() => {
    if (!attachmentId || !recordFieldsKey) return;
    // One request per extraction: a re-render, or reopening the tab, does not re-read.
    if (fieldsRequestRef.current === recordFieldsKey) return;
    fieldsRequestRef.current = recordFieldsKey;
    setFieldsErrorKey(undefined);

    CallService.getCadRecordFields(callId, attachmentId)
      .then((result) => {
        if (fieldsRequestRef.current !== recordFieldsKey) return;
        setFieldsState({ key: recordFieldsKey, fields: result });
      })
      .catch(() => {
        if (fieldsRequestRef.current !== recordFieldsKey) return;
        // Allow an explicit retry to request again.
        fieldsRequestRef.current = undefined;
        setFieldsErrorKey(recordFieldsKey);
      });
  }, [callId, attachmentId, recordFieldsKey]);

  const { reset: resetExtractWait } = extractWait;
  const { reset: resetVerifyWait } = verifyWait;
  const { reset: resetRescoreWait } = rescoreWait;

  const attach = useCallback(
    async (file: File) => {
      setUpload({ callId, progress: 0 });
      try {
        const created = await CallService.attachCadRecord(callId, file, (progress) =>
          setUpload({ callId, progress })
        );
        verificationRequestRef.current = undefined;
        // Idle with the new row Pending: the auto-start effects begin the waits from its state.
        resetExtractWait();
        resetVerifyWait();
        resetRescoreWait();
        setAttachments((prev) => [...(prev ?? []).filter((a) => a.kind !== created.kind), created]);
        return created;
      } catch {
        // Surfaced by the service layer's notification.
        return null;
      } finally {
        setUpload(undefined);
      }
    },
    [callId, setAttachments, resetExtractWait, resetVerifyWait, resetRescoreWait]
  );

  const attachFromInbox = useCallback(
    async (inboxItemId: string) => {
      try {
        const created = await CallService.attachCadFromInbox(callId, inboxItemId);
        // The same hand-off as attach: idle with the new row Pending, and the auto-start effects
        // begin the waits from its state.
        verificationRequestRef.current = undefined;
        resetExtractWait();
        resetVerifyWait();
        resetRescoreWait();
        setAttachments((prev) => [...(prev ?? []).filter((a) => a.kind !== created.kind), created]);
        return created;
      } catch {
        // Surfaced by the service layer's notification.
        return null;
      }
    },
    [callId, setAttachments, resetExtractWait, resetVerifyWait, resetRescoreWait]
  );

  const retryExtraction = useCallback(async () => {
    if (!attachmentId) return;
    try {
      const updated = await CallService.requestCadExtraction(callId, attachmentId);
      resetExtractWait();
      replaceRow(updated.attachmentId, () => updated);
    } catch {
      // Surfaced by the service layer's notification.
    }
  }, [callId, attachmentId, replaceRow, resetExtractWait]);

  // Read once per verification, only when a view asks, as for the fields.
  const loadVerification = useCallback(() => {
    if (!attachmentId || !recordVerificationKey) return;
    if (verificationRequestRef.current === recordVerificationKey) return;
    verificationRequestRef.current = recordVerificationKey;
    setVerificationErrorKey(undefined);

    CallService.getCadVerification(callId, attachmentId)
      .then((dto) => {
        if (verificationRequestRef.current !== recordVerificationKey) return;
        // Not Verified any more (re-run or re-extracted since the list was read): the patch moves
        // the row to its real status, and clearing the marker lets the next Verified load.
        if (!keepVerificationResult(dto)) verificationRequestRef.current = undefined;
        patchVerification(dto);
      })
      .catch(() => {
        if (verificationRequestRef.current !== recordVerificationKey) return;
        verificationRequestRef.current = undefined;
        setVerificationErrorKey(recordVerificationKey);
      });
  }, [callId, attachmentId, recordVerificationKey, keepVerificationResult, patchVerification]);

  const requestVerification = useCallback(async () => {
    if (!attachmentId) return false;
    try {
      const updated = await CallService.requestCadVerification(callId, attachmentId);
      // A result load still in flight belongs to the check this one replaces; clearing its marker
      // makes it discard its answer instead of putting the row back to the old Verified state.
      verificationRequestRef.current = undefined;
      // Idle with the row now Pending: the auto-start effect begins the wait.
      resetVerifyWait();
      replaceRow(updated.attachmentId, () => updated);
      return true;
    } catch {
      // Surfaced by the service layer's notification.
      return false;
    }
  }, [callId, attachmentId, replaceRow, resetVerifyWait]);

  const requestRescore = useCallback(async () => {
    if (!attachmentId) return false;
    try {
      const updated = await CallService.requestCadRescore(callId, attachmentId);
      resetRescoreWait();
      replaceRow(updated.attachmentId, () => updated);
      return true;
    } catch {
      // Surfaced by the service layer's notification.
      return false;
    }
  }, [callId, attachmentId, replaceRow, resetRescoreWait]);

  const refresh = useCallback(() => {
    CallService.listCadRecords(callId)
      .then((list) => setAttachments((prev) => sameOrLatest(prev, list)))
      .catch(() => undefined);
  }, [callId, setAttachments]);

  const apply = useCallback(
    async (fields: CadApplyField[]) => {
      if (!attachmentId) return null;
      let result: CadApplyResult;
      try {
        result = await CallService.applyCadRecord(callId, attachmentId, fields);
      } catch {
        // Surfaced by the service layer's notification.
        return null;
      }
      // The applied stamp and who applied it come from the unaudited list; a failure here only
      // leaves the stamp stale, the apply itself is done.
      refresh();
      return result;
    },
    [callId, attachmentId, refresh]
  );

  // Only hand out fields and a result that belong to the record's current extraction and check.
  const fields = fieldsState && fieldsState.key === recordFieldsKey ? fieldsState.fields : undefined;
  const fieldsError = fieldsErrorKey !== undefined && fieldsErrorKey === recordFieldsKey;
  const verification =
    verificationState && verificationState.key === recordVerificationKey
      ? verificationState.result
      : undefined;
  const verificationLoadError =
    verificationErrorKey !== undefined && verificationErrorKey === recordVerificationKey;

  const { resume: resumePolling } = extractWait;
  const { resume: resumeVerificationPolling } = verifyWait;
  const { resume: resumeRescorePolling } = rescoreWait;

  // One object per distinct state, not per render: the call page publishes this into CallContext
  // for the assistant (leg 5), and an effect keyed on it must not fire on every render.
  return useMemo(
    () => ({
      attachments,
      record,
      pollState,
      uploadProgress,
      fields,
      fieldsError,
      loadFields,
      attach,
      attachFromInbox,
      retryExtraction,
      resumePolling,
      verificationPollState,
      verification,
      verificationLoadError,
      loadVerification,
      requestVerification,
      resumeVerificationPolling,
      apply,
      appliedAutomaticallyKey,
      rescorePollState,
      requestRescore,
      resumeRescorePolling,
      rescoredKey,
      refresh,
    }),
    [
      attachments,
      record,
      pollState,
      uploadProgress,
      fields,
      fieldsError,
      loadFields,
      attach,
      attachFromInbox,
      retryExtraction,
      resumePolling,
      verificationPollState,
      verification,
      verificationLoadError,
      loadVerification,
      requestVerification,
      resumeVerificationPolling,
      apply,
      appliedAutomaticallyKey,
      rescorePollState,
      requestRescore,
      resumeRescorePolling,
      rescoredKey,
      refresh,
    ]
  );
}
